import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { photoUrl } from "@/components/LocationParts";
import {
  MapPin, Plus, Trash2, X, Loader2, Pencil, Clock, Users, Eye, EyeOff, ExternalLink, Calendar, Search, ImagePlus, Link2,
} from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const inputCls = "w-full rounded-lg bg-ink-900 border border-white/12 px-3.5 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none text-sm";

const blankSession = () => ({ id: null, class_name: "I•AM:OPEN", day: "Tuesday", time: "19:00", dates: "", description: "", price: 12, capacity: "" });
const blankLocation = () => ({ id: null, name: "", studio: "", address: "", postcode: "", schedule_note: "", capacity: "", lat: "", lon: "", published: true, external: false, external_url: "", order: 100, timetable: [] });

function PhotoField({ loc, onChange }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (!loc.id) return <p className="text-mist text-xs">Save the location first, then you can add a photo.</p>;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setMsg("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/locations/${loc.id}/photo`, fd);
      onChange(data.photo_path);
    } catch (e) { setMsg(e.response?.data?.detail || "Upload failed — please try again."); }
    finally { setBusy(false); }
  };
  const remove = async () => { await api.delete(`/admin/locations/${loc.id}/photo`); onChange(null); };
  const img = photoUrl(loc);
  return (
    <div className="space-y-3">
      {img && <img src={img} alt="Location" data-testid="loc-photo-preview" className="w-full aspect-[16/9] object-cover rounded-lg border border-white/8" />}
      <div className="flex items-center gap-3">
        <label className="book-cta cursor-pointer">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} {img ? "Replace photo" : "Upload photo"}
          <input data-testid="loc-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
        </label>
        {img && <button data-testid="loc-photo-remove" onClick={remove} className="text-xs text-ice/60 hover:text-red-300 transition-colors">Remove photo</button>}
      </div>
      {msg && <p className="text-xs text-red-300" data-testid="loc-photo-error">{msg}</p>}
      <p className="text-mist text-xs">JPG, PNG or WebP, up to 10MB. A wide (landscape) photo works best.</p>
    </div>
  );
}

function CopyLink({ id }) {
  const [done, setDone] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(`${window.location.origin}/book/${id}`); setDone(true); setTimeout(() => setDone(false), 2000); };
  return <button data-testid={`location-copy-link-${id}`} onClick={copy} className="p-2 rounded-lg text-ice/70 hover:text-cyan hover:bg-ink-700 transition-colors" title="Copy booking link">{done ? <span className="text-xs text-cyan">Copied</span> : <Link2 size={15} />}</button>;
}

function Field({ label, children }) {
  return <div><label className="label block mb-1.5">{label}</label>{children}</div>;
}

export function LocationsManager() {
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState("");

  const load = () => api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setGeoMsg(""); setEditing(blankLocation()); };
  const openEdit = (l) => { setGeoMsg(""); setEditing({
    ...blankLocation(), ...l,
    capacity: l.capacity ?? "", lat: l.lat ?? "", lon: l.lon ?? "", postcode: l.postcode ?? "",
    timetable: (l.timetable || []).map((s) => ({ ...blankSession(), ...s, capacity: s.capacity ?? "", dates: (s.dates || []).join(", ") })),
  }); };

  const removeLocation = async (l) => {
    if (!window.confirm(`Delete "${l.name}" and its timetable? This cannot be undone.`)) return;
    await api.delete(`/admin/locations/${l.id}`); load();
  };

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setSession = (i, k, v) => setEditing((e) => {
    const t = [...e.timetable]; t[i] = { ...t[i], [k]: v }; return { ...e, timetable: t };
  });
  const addSession = () => setEditing((e) => ({ ...e, timetable: [...e.timetable, blankSession()] }));
  const removeSession = (i) => setEditing((e) => ({ ...e, timetable: e.timetable.filter((_, x) => x !== i) }));

  const lookupPostcode = async () => {
    const pc = (editing.postcode || "").trim();
    const q = pc ? `${pc}, UK` : [editing.address, editing.name, "London UK"].filter(Boolean).join(", ");
    if (!q.trim()) { setGeoMsg("Enter a postcode first."); return; }
    setGeoBusy(true); setGeoMsg("");
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } });
      const data = await res.json();
      if (data && data[0]) {
        setEditing((e) => ({ ...e, lat: Number(data[0].lat).toFixed(6), lon: Number(data[0].lon).toFixed(6) }));
        setGeoMsg("Pin found — check the preview below.");
      } else setGeoMsg("Couldn't find that postcode. Try a nearby one or the full address.");
    } catch {
      setGeoMsg("Lookup failed — please try again.");
    } finally { setGeoBusy(false); }
  };
  const clearPin = () => setEditing((e) => ({ ...e, lat: "", lon: "" }));

  const hasCoords = editing && editing.lat !== "" && editing.lat != null && editing.lon !== "" && editing.lon != null;
  const mapSrc = () => {
    const lat = Number(editing.lat), lon = Number(editing.lon);
    const dLon = 0.011, dLat = 0.006;
    const bbox = [lon - dLon, lat - dLat, lon + dLon, lat + dLat].join("%2C");
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lon}`;
  };

  const save = async () => {
    const payload = {
      name: editing.name.trim(), studio: editing.studio, address: editing.address,
      postcode: editing.postcode,
      schedule_note: editing.schedule_note,
      capacity: editing.capacity === "" || editing.capacity == null ? null : Number(editing.capacity),
      lat: editing.lat === "" || editing.lat == null ? null : Number(editing.lat),
      lon: editing.lon === "" || editing.lon == null ? null : Number(editing.lon),
      published: editing.published, external: editing.external, external_url: editing.external_url,
      order: Number(editing.order) || 100,
      timetable: editing.timetable.map((s) => ({
        id: s.id || undefined, class_name: s.class_name, day: s.day, time: s.time,
        dates: (s.dates || "").split(",").map((x) => x.trim()).filter(Boolean),
        description: s.description, price: Number(s.price) || 0,
        capacity: s.capacity === "" || s.capacity == null ? null : Number(s.capacity),
      })),
    };
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/locations/${editing.id}`, payload);
      else await api.post("/admin/locations", payload);
      setEditing(null); load();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-locations">
      <div className="flex items-center justify-between">
        <p className="text-ice/70 text-sm max-w-2xl">Add or edit the studios, their class times and spaces. Unpublished locations stay hidden from the public site until you're ready.</p>
        <button data-testid="location-add" onClick={openNew} className="book-cta shrink-0"><Plus size={16} /> Add location</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {locations.map((l) => (
          <div key={l.id} data-testid={`location-card-${l.id}`} className="rounded-2xl bg-ink-800 border border-white/8 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-cyan shrink-0" />
                  <h3 className="font-display text-lg font-bold text-white truncate">{l.name}</h3>
                </div>
                <p className="text-mist text-sm mt-0.5 truncate">{l.studio || "Studio to be confirmed"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <CopyLink id={l.id} />
                <button data-testid={`location-edit-${l.id}`} onClick={() => openEdit(l)} className="p-2 rounded-lg text-ice/70 hover:text-cyan hover:bg-ink-700 transition-colors" title="Edit"><Pencil size={15} /></button>
                <button data-testid={`location-delete-${l.id}`} onClick={() => removeLocation(l)} className="p-2 rounded-lg text-red-300/80 hover:text-red-300 hover:bg-ink-700 transition-colors" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${l.published ? "bg-cyan/15 text-cyan" : "bg-white/8 text-mist"}`}>
                {l.published ? <Eye size={12} /> : <EyeOff size={12} />} {l.published ? "Published" : "Hidden"}
              </span>
              {l.external && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-white/8 text-ice/80"><ExternalLink size={12} /> External booking</span>}
              {l.capacity != null && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-white/8 text-ice/80"><Users size={12} /> {l.capacity} spaces</span>}
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-white/8 text-ice/80"><Clock size={12} /> {(l.timetable || []).length} session{(l.timetable || []).length === 1 ? "" : "s"}</span>
            </div>
          </div>
        ))}
        {locations.length === 0 && <p className="text-mist text-sm">No locations yet. Add one to get started.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 sm:p-8" data-testid="location-editor">
          <div className="w-full max-w-2xl rounded-2xl bg-ink-800 border border-white/12 my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 sticky top-0 bg-ink-800 rounded-t-2xl z-10">
              <h3 className="font-display text-xl font-bold text-white">{editing.id ? "Edit location" : "New location"}</h3>
              <button data-testid="location-editor-close" onClick={() => setEditing(null)} className="p-2 text-ice/70 hover:text-white"><X size={22} /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Location name"><input data-testid="loc-name" className={inputCls} value={editing.name} onChange={(e) => setF("name", e.target.value)} placeholder="Green Lanes" /></Field>
                <Field label="Studio / venue"><input data-testid="loc-studio" className={inputCls} value={editing.studio} onChange={(e) => setF("studio", e.target.value)} placeholder="K5 Studio" /></Field>
              </div>
              <Field label="Location photo"><PhotoField loc={editing} onChange={(p) => { setF("photo_path", p); load(); }} /></Field>
              <Field label="Address"><input data-testid="loc-address" className={inputCls} value={editing.address} onChange={(e) => setF("address", e.target.value)} placeholder="71 Ashfield Road, London, N4 1FF" /></Field>

              <div className="rounded-xl bg-ink-900 border border-white/8 p-4 space-y-3">
                <p className="label">Map location (optional)</p>
                <div className="flex gap-2">
                  <input data-testid="loc-postcode" className={inputCls} value={editing.postcode}
                    onChange={(e) => setF("postcode", e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupPostcode(); } }}
                    placeholder="Postcode, e.g. N4 1FF" />
                  <button data-testid="loc-lookup" onClick={lookupPostcode} disabled={geoBusy} className="book-cta shrink-0">
                    {geoBusy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find
                  </button>
                </div>
                {geoMsg && <p className="text-xs text-cyan/80">{geoMsg}</p>}
                {hasCoords && (
                  <div className="relative aspect-[16/7] w-full overflow-hidden rounded-lg bg-ink-800" data-testid="loc-map-preview">
                    <iframe title="Map preview" src={mapSrc()} className="absolute inset-0 h-full w-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                )}
                {hasCoords
                  ? <button data-testid="loc-clear-pin" onClick={clearPin} className="text-xs text-ice/60 hover:text-red-300 transition-colors">Clear map pin</button>
                  : <p className="text-mist text-xs">Search a postcode to drop a map pin. Leave blank to show "Location to be confirmed".</p>}
              </div>
              <Field label="Schedule note (dates line shown on the site)"><input data-testid="loc-schedule" className={inputCls} value={editing.schedule_note} onChange={(e) => setF("schedule_note", e.target.value)} placeholder="7–9pm · every Tuesday until December" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Default spaces per class"><input data-testid="loc-capacity" type="number" min={0} className={inputCls} value={editing.capacity} onChange={(e) => setF("capacity", e.target.value)} placeholder="12" /></Field>
                <Field label="Display order"><input data-testid="loc-order" type="number" className={inputCls} value={editing.order} onChange={(e) => setF("order", e.target.value)} /></Field>
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm text-ice/85 cursor-pointer">
                  <input data-testid="loc-published" type="checkbox" checked={editing.published} onChange={(e) => setF("published", e.target.checked)} className="accent-cyan h-4 w-4" /> Published (visible on site)
                </label>
                <label className="flex items-center gap-2 text-sm text-ice/85 cursor-pointer">
                  <input data-testid="loc-external" type="checkbox" checked={editing.external} onChange={(e) => setF("external", e.target.checked)} className="accent-cyan h-4 w-4" /> Booked externally
                </label>
              </div>
              {editing.external && (
                <Field label="External booking URL"><input data-testid="loc-external-url" className={inputCls} value={editing.external_url} onChange={(e) => setF("external_url", e.target.value)} placeholder="https://..." /></Field>
              )}

              {!editing.external && (
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-3">
                    <p className="label">Class sessions</p>
                    <button data-testid="session-add" onClick={addSession} className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg border border-cyan/40 text-cyan px-3 py-1.5 hover:bg-cyan/10 transition-colors"><Plus size={14} /> Add session</button>
                  </div>
                  <div className="space-y-3">
                    {editing.timetable.map((s, i) => (
                      <div key={i} data-testid={`session-row-${i}`} className="rounded-xl bg-ink-900 border border-white/8 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-mist">Session {i + 1}</span>
                          <button data-testid={`session-remove-${i}`} onClick={() => removeSession(i)} className="text-red-300/80 hover:text-red-300"><Trash2 size={14} /></button>
                        </div>
                        <Field label="Class name (customisable I•AM…)"><input data-testid={`session-name-${i}`} className={inputCls} value={s.class_name} onChange={(e) => setSession(i, "class_name", e.target.value)} placeholder="I•AM:OPEN" list="iam-class-suggestions" /></Field>
                        <div className="grid sm:grid-cols-3 gap-3">
                          <Field label="Weekly day">
                            <select data-testid={`session-day-${i}`} className={inputCls} value={s.day} onChange={(e) => setSession(i, "day", e.target.value)}>
                              <option value="">—</option>
                              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </Field>
                          <Field label="Time"><input data-testid={`session-time-${i}`} className={inputCls} value={s.time} onChange={(e) => setSession(i, "time", e.target.value)} placeholder="19:00" /></Field>
                          <Field label="Price (£)"><input data-testid={`session-price-${i}`} type="number" min={0} className={inputCls} value={s.price} onChange={(e) => setSession(i, "price", e.target.value)} /></Field>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Field label="Bookable dates (comma-separated, e.g. 3.11, 10.11 — leave blank to run every week)"><input data-testid={`session-dates-${i}`} className={inputCls} value={s.dates} onChange={(e) => setSession(i, "dates", e.target.value)} placeholder="3.11, 10.11, 17.11" /></Field>
                          <Field label="Spaces"><input data-testid={`session-capacity-${i}`} type="number" min={0} className={inputCls} value={s.capacity} onChange={(e) => setSession(i, "capacity", e.target.value)} placeholder="12" /></Field>
                        </div>
                        <Field label="Class description"><input data-testid={`session-desc-${i}`} className={inputCls} value={s.description} onChange={(e) => setSession(i, "description", e.target.value)} placeholder="The complete method in one session." /></Field>
                      </div>
                    ))}
                    {editing.timetable.length === 0 && <p className="text-mist text-sm">No sessions yet. Add one, or leave empty to show "times to be confirmed".</p>}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/8 sticky bottom-0 bg-ink-800 rounded-b-2xl">
              <button data-testid="location-cancel" onClick={() => setEditing(null)} className="text-sm text-ice/70 hover:text-white px-4 py-2">Cancel</button>
              <button data-testid="location-save" onClick={save} disabled={saving || !editing.name.trim()} className="book-cta disabled:opacity-50">
                {saving ? <Loader2 size={16} className="animate-spin" /> : null} Save location
              </button>
            </div>
          </div>
          <datalist id="iam-class-suggestions">
            {["I•AM:OPEN", "I•AM:ENERGISE", "I•AM:MOBILITY", "I•AM:NEURO", "I•AM:RESTORE", "I•AM:ACTIVATE", "I•AM:FREE"].map((n) => <option key={n} value={n} />)}
          </datalist>
        </div>
      )}
    </div>
  );
}

export default LocationsManager;
